// src/services/authService.js
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  applyActionCode,
  checkActionCode
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { userService } from './userService';

export const authService = {
  /**
   * Sign up with email and password
   */
  async signUpWithEmail(email, password, userData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update display name
      await updateProfile(user, {
        displayName: userData.name
      });
      
      // Send verification email
      await sendEmailVerification(user, {
        url: window.location.origin + '/#/auth?verified=true',
        handleCodeInApp: true
      });
      
      // Create user profile in Firestore
      await userService.saveUser(user.uid, {
        name: userData.name,
        email: user.email,
        mobile: userData.mobile,
        roles: userData.roles || ['LENDER'],
        isVerified: false,
        emailVerified: false,
        createdAt: new Date().toISOString()
      });
      
      return { success: true, user, emailVerificationSent: true };
    } catch (error) {
      console.error('Error signing up:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update email verification status in Firestore if needed
      if (user.emailVerified) {
        const profile = await userService.getUser(user.uid);
        if (profile && !profile.emailVerified) {
          await userService.saveUser(user.uid, {
            emailVerified: true
          });
        }
      }
      
      return { success: true, user, emailVerified: user.emailVerified };
    } catch (error) {
      console.error('Error signing in:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * Sign in with Google
   */
  async signInWithGoogle(additionalData = {}) {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if user exists
      const existingUser = await userService.getUser(user.uid);
      
      // Create user profile if first time
      if (!existingUser) {
        await userService.saveUser(user.uid, {
          name: user.displayName,
          email: user.email,
          mobile: additionalData.mobile || '',
          roles: additionalData.roles || ['LENDER'],
          isVerified: false,
          emailVerified: true, // Google emails are pre-verified
          createdAt: new Date().toISOString()
        });
      } else if (!existingUser.emailVerified) {
        // Update email verification status for existing Google users
        await userService.saveUser(user.uid, {
          emailVerified: true
        });
      }
      
      return { success: true, user, isNewUser: !existingUser };
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user signed in');
      }
      
      if (user.emailVerified) {
        throw new Error('Email already verified');
      }
      
      await sendEmailVerification(user, {
        url: window.location.origin + '/#/auth?verified=true',
        handleCodeInApp: true
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  },

  /**
   * Verify email with action code
   */
  async verifyEmail(actionCode) {
    try {
      await applyActionCode(auth, actionCode);
      
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        await userService.saveUser(user.uid, {
          emailVerified: true
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error;
    }
  },

  /**
   * Check action code validity
   */
  async checkActionCode(actionCode) {
    try {
      const info = await checkActionCode(auth, actionCode);
      return { success: true, info };
    } catch (error) {
      console.error('Error checking action code:', error);
      throw error;
    }
  },

  /**
   * Sign out
   */
  async signOut() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  /**
   * Get current user
   */
  getCurrentUser() {
    return auth.currentUser;
  },

  /**
   * Auth state observer
   */
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  },

  /**
   * Handle auth errors
   */
  handleAuthError(error) {
    const errorMessages = {
      'auth/email-already-in-use': 'This email is already registered.',
      'auth/invalid-email': 'Invalid email address.',
      'auth/operation-not-allowed': 'Operation not allowed.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account found with this email.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/popup-closed-by-user': 'Sign-in popup was closed.',
      'auth/cancelled-popup-request': 'Only one popup request is allowed at a time.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/invalid-action-code': 'Invalid or expired verification link.'
    };
    
    return {
      code: error.code,
      message: errorMessages[error.code] || error.message
    };
  }
};